/**
 * Hook that manages per-defect SkyWrench troubleshooting results for an event
 * modal.
 *
 * SkyWrench analysis is asynchronous and runs server-side for several minutes,
 * independent of the browser. This hook submits a job, immediately persists the
 * pending state onto the event record (so it survives modal/session close and
 * is visible to other users), and polls for completion while the modal is open.
 * On open it resumes polling any persisted pending jobs. A backend poller
 * materializes results even when no modal is open, so the user never has to
 * wait or re-run.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { UnscheduledEvent, SkyWrenchResult } from '../../types';
import { formatAtaCode } from '../../services/ataCodeService';
import { FleetRecord, getAircraftTypeByTail } from '../../services/fleetService';
import {
  submitSkyWrenchJob,
  persistSkyWrenchResult,
  buildSkyWrenchPending,
  buildSkyWrenchError,
  SkyWrenchQueryInput,
} from '../../services/skyWrenchService';
import { pollJobToCompletion, inputFromResult } from './skyWrenchPolling';

/** A defect as tracked in the modal's local editing state. */
interface ModalDefect {
  id: number;
  text: string;
}

/** Inputs the hook needs from the modal to build a SkyWrench query. */
export interface UseSkyWrenchOptions {
  isOpen: boolean;
  event: UnscheduledEvent | null;
  formData: UnscheduledEvent | null;
  defects: ModalDefect[];
  selectedChapters: Record<number, string>;
  selectedSections: Record<number, string>;
  fleetData: FleetRecord[];
}

/** Return shape for {@link useSkyWrench}. */
export interface UseSkyWrenchResult {
  skyWrenchResults: Record<number, SkyWrenchResult>;
  setSkyWrenchResults: React.Dispatch<React.SetStateAction<Record<number, SkyWrenchResult>>>;
  triggerSkyWrench: (
    defectId: number,
    defectTextOverride?: string,
    ataCodeOverride?: string,
  ) => void;
}

/**
 * Resolves the ATA code to send for a defect. Prefers an explicit override
 * (used when the value was just changed); otherwise formats the defect's
 * selected chapter/section. The em-dash placeholder is normalized to ''.
 */
function resolveAtaCode(
  defectId: number,
  selectedChapters: Record<number, string>,
  selectedSections: Record<number, string>,
  ataCodeOverride?: string,
): string {
  const formatted =
    ataCodeOverride !== undefined
      ? ataCodeOverride
      : formatAtaCode(selectedChapters[defectId] || '', selectedSections[defectId] || '');
  return formatted === '—' ? '' : formatted;
}

/**
 * Builds the SkyWrench query input for a defect from the modal's current state,
 * deriving aircraftType from the tail when the record doesn't carry it.
 */
function buildQueryInput(
  defectText: string,
  ataCode: string,
  formData: UnscheduledEvent,
  fleetData: FleetRecord[],
): SkyWrenchQueryInput {
  return {
    aircraftType: formData.aircraftType || getAircraftTypeByTail(fleetData, formData.tailNumber),
    ataCode,
    defectDescription: defectText,
    tailNumber: formData.tailNumber,
    carrier: formData.carrier,
    station: formData.station,
  };
}

/** State-setter type alias used by the module-level orchestration helpers. */
type ResultsSetter = React.Dispatch<React.SetStateAction<Record<number, SkyWrenchResult>>>;

/**
 * Drives a single job's poll loop to completion and folds the terminal result
 * into state (only while still active). Tracks the job id to avoid double-polls.
 */
function runPoll(
  jobId: string,
  defectId: number,
  input: SkyWrenchQueryInput,
  ctx: {
    eventId: string;
    setResults: ResultsSetter;
    polling: Set<string>;
    isActive: () => boolean;
  },
): void {
  if (!jobId || ctx.polling.has(jobId)) return;
  ctx.polling.add(jobId);
  pollJobToCompletion(jobId, defectId, input, ctx.eventId)
    .then((terminal) => {
      if (terminal && ctx.isActive()) {
        ctx.setResults((prev) => ({ ...prev, [defectId]: terminal }));
      }
    })
    .finally(() => {
      ctx.polling.delete(jobId);
    });
}

/**
 * Submits a job, persists the pending state to the event record, and starts a
 * poll loop via {@link startPollFn}. On submit failure it records and persists
 * an error result. Kept at module scope so the hook body stays small.
 */
function runSubmitAndPoll(
  defectId: number,
  input: SkyWrenchQueryInput,
  eventId: string,
  setResults: ResultsSetter,
  startPollFn: (jobId: string, defectId: number, input: SkyWrenchQueryInput) => void,
): void {
  submitSkyWrenchJob(input)
    .then((job) => {
      if (!job.jobId) throw new Error('SkyWrench did not return a job id');
      const pending = buildSkyWrenchPending(defectId, input, job.jobId);
      setResults((prev) => ({ ...prev, [defectId]: pending }));
      // Persist pending so the run survives modal close and the backend poller
      // can materialize it even if this browser goes away.
      void persistSkyWrenchResult(eventId, pending);
      startPollFn(job.jobId, defectId, input);
    })
    .catch((err: unknown) => {
      const message = err instanceof Error ? err.message : 'Unknown error';
      const errored = buildSkyWrenchError(defectId, input, message);
      setResults((prev) => ({ ...prev, [defectId]: errored }));
      void persistSkyWrenchResult(eventId, errored);
    });
}

/**
 * Seeds local state from persisted results and resumes polling any pending
 * jobs. Returns the seeded map's pending entries count (unused by callers but
 * keeps the helper a pure-ish unit). Marks the active ref true.
 */
function seedAndResume(
  seeded: Record<number, SkyWrenchResult>,
  setResults: ResultsSetter,
  startPollFn: (jobId: string, defectId: number, input: SkyWrenchQueryInput) => void,
): void {
  setResults(seeded);
  for (const result of Object.values(seeded)) {
    if (result.status === 'pending' && result.jobId) {
      startPollFn(result.jobId, result.defectId, inputFromResult(result));
    }
  }
}

/**
 * Manages SkyWrench results state, the per-defect query trigger, and polling.
 */
export function useSkyWrench(options: UseSkyWrenchOptions): UseSkyWrenchResult {
  const { isOpen, event, formData, defects, selectedChapters, selectedSections, fleetData } =
    options;
  const [skyWrenchResults, setSkyWrenchResults] = useState<Record<number, SkyWrenchResult>>({});

  // Tracks job ids currently being polled (no double-polls) and whether the
  // modal is still open (so in-flight polls stop updating state after close).
  const pollingJobIds = useRef<Set<string>>(new Set());
  const isActiveRef = useRef<boolean>(false);
  const eventId = formData?.eventId || '';

  const startPoll = useCallback(
    (jobId: string, defectId: number, input: SkyWrenchQueryInput): void =>
      runPoll(jobId, defectId, input, {
        eventId,
        setResults: setSkyWrenchResults,
        polling: pollingJobIds.current,
        isActive: () => isActiveRef.current,
      }),
    [eventId],
  );

  // Seed from persisted values on open, and resume polling any pending jobs.
  useEffect(() => {
    if (!isOpen) {
      isActiveRef.current = false;
      return;
    }
    isActiveRef.current = true;
    seedAndResume(event?.skyWrenchResults || {}, setSkyWrenchResults, startPoll);
    return (): void => {
      isActiveRef.current = false;
    };
  }, [isOpen, event, startPoll]);

  const triggerSkyWrench = useCallback(
    (defectId: number, defectTextOverride?: string, ataCodeOverride?: string): void => {
      if (!formData) return;
      const defect = defects.find((d) => d.id === defectId);
      const defectText = defectTextOverride || defect?.text || '';
      if (!defectText) return;

      const ataCode = resolveAtaCode(defectId, selectedChapters, selectedSections, ataCodeOverride);
      const input = buildQueryInput(defectText, ataCode, formData, fleetData);
      runSubmitAndPoll(defectId, input, eventId, setSkyWrenchResults, startPoll);
    },
    [formData, defects, selectedChapters, selectedSections, fleetData, eventId, startPoll],
  );

  return { skyWrenchResults, setSkyWrenchResults, triggerSkyWrench };
}
